import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Check, Loader2, X, Zap } from "lucide-react";
import { toast } from "sonner";
import { kaiCheckout, kaiCheckoutPasses, kaiFoundingSeats } from "@/lib/api";

/**
 * Upgrade modal for the Complete System (PRIMARY) and Kai Unlimited (secondary).
 * Props:
 *   email: user email (required)
 *   pricing: { tier, price_int, label, seats_remaining, paid_users_count, founding_cap, plans }
 *   reason: "limit" | "soft" — controls headline copy
 *   onClose: close handler
 */
function isReturningUser() {
  try { return !!localStorage.getItem("kai_session_v2") || !!localStorage.getItem("tracker_session_v1"); }
  catch (e) { return false; }
}

const KaiUpgradeModal = ({ email, pricing, passesPrice = 2, passesCount = 10, reason = "limit", onClose }) => {
  const [loading, setLoading] = useState("");        // "complete" | "kai" | ""
  const [passesLoading, setPassesLoading] = useState(false);
  const [billingInterval, setBillingInterval] = useState(isReturningUser() ? "year" : "month");
  const [seats, setSeats] = useState(null);

  useEffect(() => {
    kaiFoundingSeats().then((r) => setSeats(r.data)).catch(() => {});
  }, []);

  if (!pricing) return null;

  const headline = reason === "limit"
    ? "You've reached your daily limit"
    : "Unlock the Complete System";

  const completePlan = pricing.plans?.complete?.[billingInterval]
    || { price_usd: billingInterval === "year" ? 290 : 29 };
  const kaiPlan = pricing.plans?.kai?.[billingInterval]
    || { price_usd: pricing.price_int || (billingInterval === "year" ? 190 : 19) };

  const startCheckout = async (plan) => {
    if (!email) { toast.error("We need your email to continue."); return; }
    setLoading(plan);
    try {
      const res = await kaiCheckout({ email, origin_url: window.location.origin, plan, interval: billingInterval });
      if (res.data?.already_paid) {
        toast.success("You're already subscribed!");
        onClose?.();
        return;
      }
      if (res.data?.url) { window.location.href = res.data.url; return; }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (e) {
      const msg = e?.response?.data?.detail || "Checkout failed. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Checkout failed.");
    } finally { setLoading(""); }
  };

  const startPassesCheckout = async () => {
    if (!email) { toast.error("We need your email to continue."); return; }
    setPassesLoading(true);
    try {
      const res = await kaiCheckoutPasses({ email, origin_url: window.location.origin });
      if (res.data?.url) { window.location.href = res.data.url; return; }
      toast.error("Couldn't start checkout. Please try again.");
    } catch (e) {
      toast.error("Checkout failed. Please try again.");
    } finally { setPassesLoading(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center px-4 py-6"
      onClick={onClose}
      data-testid="kai-upgrade-modal"
      role="dialog"
      aria-modal="true"
    >
      <motion.div
        initial={{ scale: 0.95, y: 20, opacity: 0 }}
        animate={{ scale: 1, y: 0, opacity: 1 }}
        exit={{ scale: 0.95, y: 20, opacity: 0 }}
        transition={{ type: "spring", damping: 24, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl relative max-h-[90vh] overflow-y-auto"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          data-testid="kai-upgrade-close"
          className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-[#F0EDE6] transition-colors"
          style={{ color: "#8A7D55" }}
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex items-center gap-3 mb-4">
          <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: "#2D6A4F" }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold leading-tight" style={{ color: "#1A1A1A", fontFamily: "Fraunces, serif" }}>
              {headline}
            </h2>
            <p className="text-xs" style={{ color: "#8A7D55" }}>
              The full system: Tracker + Kai together.
            </p>
          </div>
        </div>

        {/* Monthly · Yearly toggle */}
        <div
          className="flex p-1 rounded-full bg-[#F0EDE6] mb-4"
          role="tablist"
          data-testid="kai-modal-interval-toggle"
        >
          <button
            onClick={() => setBillingInterval("month")}
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-colors ${billingInterval === "month" ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#8A7D55]"}`}
            data-testid="kai-modal-interval-month"
            aria-pressed={billingInterval === "month"}
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingInterval("year")}
            className={`flex-1 py-1.5 rounded-full text-xs font-semibold transition-colors ${billingInterval === "year" ? "bg-white text-[#1A1A1A] shadow-sm" : "text-[#8A7D55]"}`}
            data-testid="kai-modal-interval-year"
            aria-pressed={billingInterval === "year"}
          >
            Yearly · save 2 months
          </button>
        </div>

        {/* PRIMARY — Complete System (visually dominant) */}
        <div
          className="rounded-2xl p-5 mb-4 relative"
          style={{ background: "linear-gradient(135deg, #1B4332 0%, #2D6A4F 100%)", color: "#FFFFFF", borderColor: "#D4A373" }}
          data-testid="kai-modal-complete-card"
        >
          <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-3 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-[#D4A373] text-[#1A2018]">
            Recommended
          </span>
          <p className="text-[10px] uppercase tracking-wider mb-1" style={{ color: "#E9EDC9" }}>Complete System</p>
          <p className="text-3xl font-bold leading-none mb-1" style={{ fontFamily: "Fraunces, serif", color: "#FFFFFF" }}>
            ${completePlan.price_usd}
            <span className="text-sm font-normal" style={{ color: "#E9EDC9" }}>/{billingInterval === "year" ? "yr" : "mo"}</span>
          </p>
          <p className="text-xs mb-3" style={{ color: "#E9EDC9" }}>
            Tracker Pro + Kai Unlimited together
          </p>
          <ul className="space-y-1.5 text-xs mb-4" style={{ color: "#FFFFFF" }}>
            {[
              "Unlimited Kai (no caps)",
              "Daily Tracker + plateau alerts + Plateau Rescue",
              "Sunday Coach Report",
              "Kai sees your last 7 days, every chat",
            ].map((item) => (
              <li key={item} className="flex items-start gap-2">
                <Check className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: "#E9EDC9" }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
          <button
            onClick={() => startCheckout("complete")}
            disabled={!!loading || passesLoading}
            data-testid="kai-modal-complete-cta"
            className="w-full py-2.5 rounded-xl text-sm font-semibold bg-white hover:bg-[#E9EDC9] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ color: "#1B4332" }}
          >
            {loading === "complete" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {loading === "complete" ? "Starting…" : "Get the Complete System"}
          </button>
        </div>

        {/* SECONDARY — Just Kai Unlimited (smaller link) */}
        <div className="flex items-center justify-between gap-3 px-1 py-2 mb-3" data-testid="kai-modal-kai-only-row">
          <div className="text-xs">
            <p className="text-[#1A1A1A] font-semibold">Or just Kai Unlimited</p>
            <p className="text-[#8A7D55]">
              ${kaiPlan.price_usd}/{billingInterval === "year" ? "yr" : "mo"} · No Tracker Pro
              {seats?.active && billingInterval === "month" && pricing.tier === "founding" && (
                <span className="block text-[10px] mt-0.5" style={{ color: "#C95C3E", fontWeight: 600 }}>
                  {seats.remaining} of {seats.cap} founding seats remaining at $9
                </span>
              )}
            </p>
          </div>
          <button
            onClick={() => startCheckout("kai")}
            disabled={!!loading || passesLoading}
            data-testid="kai-modal-kai-only-cta"
            className="text-xs font-semibold text-[#2D6A4F] hover:underline disabled:opacity-50 whitespace-nowrap"
          >
            {loading === "kai" ? "Starting…" : "Pick this →"}
          </button>
        </div>

        {/* Pay-as-you-go escape hatch */}
        <div className="flex items-center gap-3 my-2">
          <div className="flex-1 h-px" style={{ backgroundColor: "#E8E4DC" }} />
          <span className="text-[10px] uppercase tracking-wider" style={{ color: "#8A7D55" }}>or</span>
          <div className="flex-1 h-px" style={{ backgroundColor: "#E8E4DC" }} />
        </div>
        <button
          onClick={startPassesCheckout}
          disabled={!!loading || passesLoading}
          data-testid="kai-passes-submit"
          className="w-full py-2 rounded-xl text-[11px] font-medium flex items-center justify-center gap-1.5 border transition-colors disabled:opacity-50 hover:bg-[#F0EDE6]"
          style={{ borderColor: "#E8E4DC", color: "#8A7D55", backgroundColor: "white" }}
        >
          {passesLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
          {passesLoading ? "Starting…" : `Not ready? ${passesCount} extra messages today for $${passesPrice}`}
        </button>

        <p className="text-[11px] text-center mt-3" style={{ color: "#8A7D55" }}>
          Secure checkout via Stripe · Cancel anytime
        </p>
      </motion.div>
    </motion.div>
  );
};

export default KaiUpgradeModal;
